import { useCallback, useMemo } from 'react';
import { useLanguage } from './useLanguage';

/**
 * @hook useContent
 * @description Page copy from the dictionary, with in-code defaults behind it.
 *
 * Every string on the public site is editable from Dictionary → Global
 * Settings → Site content. This hook is the read side of that: it fetches the
 * surface's namespace and layers it over the defaults the page ships with, so
 * a dictionary outage (or a field nobody has filled in yet) degrades to the
 * copy that was there before rather than to a blank page.
 *
 *   const { t, list } = useContent('about', DEFAULTS);
 *   <h1>{t('HERO.TITLE')}</h1>
 *
 * Remote values win whenever they are present. An empty string is a present
 * value — clearing a field in the admin is a deliberate act and is honoured;
 * only `undefined`/`null` fall through to the default.
 */

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const deepMerge = (base, patch) => {
  if (!isPlainObject(patch)) return base;
  const out = { ...(isPlainObject(base) ? base : {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value === null) continue;
    out[key] = isPlainObject(value) && isPlainObject(out[key])
      ? deepMerge(out[key], value)
      : value;
  }
  return out;
};

const readPath = (source, path) => String(path)
  .split('.')
  .reduce((cursor, key) => (cursor && typeof cursor === 'object' ? cursor[key] : undefined), source);

export const useContent = (namespace, defaults = {}) => {
  const remote = useLanguage(namespace);

  const content = useMemo(() => deepMerge(defaults, remote), [remote, defaults]);

  /** Read a string by dot-path. */
  const t = useCallback((path, fallback = '') => {
    const value = readPath(content, path);
    return value === undefined || value === null ? fallback : value;
  }, [content]);

  /** Read an array by dot-path — always returns an array, never undefined. */
  const list = useCallback((path, fallback = []) => {
    const value = readPath(content, path);
    return Array.isArray(value) ? value : fallback;
  }, [content]);

  return { content, t, list };
};

export default useContent;
